import {
  EditOutlined,
  FormOutlined,
  RocketOutlined,
  TagsOutlined,
  MessageOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { AInput, AModal, ASelect, ATextArea } from '../../../../components';
import { useCallback, useEffect, useState } from 'react';
import { Card, Col, Form, Row, Space, Typography } from 'antd';
import { useToast } from '../../../../hooks/toast';
import { schemaCrmMessage } from '../../schemas/yup.crm-message.schema';
import { IClient } from '../../../../shared/interfaces';
import { getSelectClients } from '../../../../http';
import {
  CrmMessageType,
  ICrmTemplate,
  getCrmTemplates,
  sendManualCrmMessage,
} from '../../../../http/crm';

const { Text } = Typography;

interface ISendModalProps {
  open: boolean;
  close: () => void;
  defaultClientId?: string | null;
  onSent?: () => void;
}

const MESSAGE_TYPE_OPTIONS: { value: CrmMessageType; label: string }[] = [
  { value: 'TEXT', label: 'Texto livre' },
  { value: 'TEMPLATE', label: 'Template' },
];

const SendModal: React.FC<ISendModalProps> = ({
  open,
  close,
  defaultClientId,
  onSent,
}) => {
  const { addToast } = useToast();

  const [clientId, setClientId] = useState<string>(defaultClientId ?? '');
  const [clients, setClients] = useState<IClient[]>([]);
  const [loadingClients, setLoadingClients] = useState<boolean>(false);

  const [messageType, setMessageType] = useState<CrmMessageType>('TEXT');
  const [templateId, setTemplateId] = useState<string>('');
  const [templates, setTemplates] = useState<ICrmTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(false);
  const [content, setContent] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);

  const searchClients = useCallback(async (query: string) => {
    setLoadingClients(true);
    try {
      const param = isNaN(Number(query)) ? 0 : Number(query);
      const data = await getSelectClients(param || query?.length ? 9999 : 200);
      const filtered = query?.length
        ? (data || []).filter(
            (c: IClient) =>
              (c.corporate_name || c.fantasy_name || '')
                .toLowerCase()
                .includes(query.toLowerCase()) ||
              String(c.code || '').includes(query) ||
              (c.cell_phone || '').replace(/\D/g, '').includes(query.replace(/\D/g, '')),
          )
        : data || [];
      setClients(filtered.slice(0, 100));
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao buscar clientes.',
      });
    } finally {
      setLoadingClients(false);
    }
  }, [addToast]);

  const loadTemplates = useCallback(async () => {
    if (messageType !== 'TEMPLATE') return;
    setLoadingTemplates(true);
    try {
      const res = await getCrmTemplates(100, 0, '', undefined, true);
      setTemplates(res.data || []);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao listar templates.',
      });
    } finally {
      setLoadingTemplates(false);
    }
  }, [messageType, addToast]);

  useEffect(() => {
    if (messageType === 'TEMPLATE') {
      loadTemplates();
    }
  }, [messageType, loadTemplates]);

  useEffect(() => {
    if (open) {
      searchClients('');
      if (defaultClientId) {
        setClientId(defaultClientId);
      }
    }
  }, [open, defaultClientId, searchClients]);

  const selectedClient = clients.find((c) => c.id === clientId);

  const handleSend = useCallback(async () => {
    const payload: Record<string, unknown> = {
      client_id: clientId || null,
      message_type: messageType,
      template_id: messageType === 'TEMPLATE' ? templateId || null : null,
      content: messageType === 'TEXT' ? content || null : null,
      source: 'MANUAL',
    };

    try {
      await schemaCrmMessage.validate(payload);
      setSending(true);
      const res = await sendManualCrmMessage(payload);
      addToast({
        type: res.queued ? 'success' : res.ok ? 'info' : 'warning',
        title: res.queued ? 'Mensagem enfileirada' : res.ok ? 'Mensagem enviada' : 'Aviso',
        description: `${res.client_name ? `${res.client_name} - ` : ''}${res.phone}`,
      });
      if (onSent) onSent();
      close();
    } catch (err: any) {
      if (err?.name === 'ValidationError') {
        addToast({
          type: 'error',
          title: 'Campos obrigatórios',
          description: err.errors?.[0] ?? 'Verifique os campos.',
        });
      } else {
        addToast({
          type: 'error',
          title: 'Erro ao enviar',
          description: err?.response?.data?.error ?? 'Erro desconhecido.',
        });
      }
    } finally {
      setSending(false);
    }
  }, [
    clientId,
    messageType,
    templateId,
    content,
    addToast,
    close,
    onSent,
  ]);

  useEffect(() => {
    if (!open) return;
    setClientId(defaultClientId ?? '');
    setMessageType('TEXT');
    setTemplateId('');
    setContent('');
  }, [open, defaultClientId]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        <>
          <RocketOutlined /> Enviar Mensagem Manual (WhatsApp)
        </>
      }
      onOk={handleSend}
      confirmLoading={sending}
      okText={sending ? 'Enviando...' : 'Enviar mensagem'}
      width={680}
    >
      <Form layout="vertical" style={{ maxWidth: '100%', marginTop: 8 }}>
        <Card
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <Space size={8}>
              <UserOutlined /> Destinatário
            </Space>
          }
        >
          <Row gutter={[12, 0]}>
            <Col xs={24}>
              <Form.Item
                label={
                  <Space size={6}>
                    <UserOutlined style={{ color: '#1890ff' }} /> Cliente
                  </Space>
                }
                required
              >
                <ASelect
                  showSearch
                  loading={loadingClients}
                  placeholder="Pesquise por nome, código ou telefone..."
                  allowClear
                  value={clientId || undefined}
                  onChange={(v) => setClientId(v ?? '')}
                  onSearch={(q: string) => searchClients(q)}
                  filterOption={false}
                  notFoundContent={loadingClients ? 'Buscando...' : 'Nenhum cliente encontrado'}
                  options={clients.map((c) => {
                    const label = [
                      c.code ? `#${c.code}` : '',
                      c.corporate_name || c.fantasy_name || '(sem nome)',
                      c.cell_phone ? `· ${c.cell_phone}` : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    return {
                      label,
                      value: c.id,
                    };
                  })}
                />
              </Form.Item>
            </Col>
            {selectedClient && (
              <Col xs={24}>
                <Card
                  size="small"
                  style={{ background: '#f0f9ff', marginBottom: 0 }}
                  title={
                    <Space size={8}>
                      <UserOutlined style={{ color: '#1890ff' }} />
                      {selectedClient.corporate_name || selectedClient.fantasy_name}
                    </Space>
                  }
                  extra={
                    selectedClient.cell_phone ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        📱 {selectedClient.cell_phone}
                      </Text>
                    ) : null
                  }
                >
                  <Row gutter={12}>
                    {selectedClient.birthday && (
                      <Col xs={12}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          🎂 Nasc.: {new Date(selectedClient.birthday).toLocaleDateString('pt-BR')}
                        </Text>
                      </Col>
                    )}
                    {selectedClient.email && (
                      <Col xs={12}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ✉️ {selectedClient.email}
                        </Text>
                      </Col>
                    )}
                  </Row>
                </Card>
              </Col>
            )}
          </Row>
        </Card>

        <Card
          size="small"
          style={{ marginBottom: 0 }}
          title={
            <Space size={8}>
              <MessageOutlined /> Mensagem
            </Space>
          }
        >
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={10}>
              <Form.Item
                label={
                  <Space size={6}>
                    <TagsOutlined style={{ color: '#2f54eb' }} /> Tipo
                  </Space>
                }
                required
              >
                <ASelect
                  value={messageType}
                  onChange={(v) => {
                    setMessageType(v as CrmMessageType);
                    setTemplateId('');
                    setContent('');
                  }}
                  options={MESSAGE_TYPE_OPTIONS}
                />
              </Form.Item>
            </Col>

            {messageType === 'TEMPLATE' && (
              <Col xs={24} sm={14}>
                <Form.Item
                  label={
                    <Space size={6}>
                      <FormOutlined style={{ color: '#eb2f96' }} /> Template
                    </Space>
                  }
                  required
                >
                  <ASelect
                    showSearch
                    loading={loadingTemplates}
                    placeholder="Selecione um template"
                    allowClear
                    value={templateId || undefined}
                    onChange={(v) => setTemplateId(v ?? '')}
                    filterOption={(input, option: any) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={templates.map((t) => ({
                      label: t.name,
                      value: t.id,
                    }))}
                  />
                </Form.Item>
              </Col>
            )}

            {messageType === 'TEXT' && (
              <Col xs={24}>
                <Form.Item
                  label={
                    <Space size={6}>
                      <EditOutlined style={{ color: '#52c41a' }} /> Conteúdo
                    </Space>
                  }
                  required
                  style={{ marginBottom: 0 }}
                >
                  <ATextArea
                    rows={6}
                    placeholder="Digite a mensagem que será enviada para o cliente..."
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    maxLength={4096}
                    showCount
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Card>
      </Form>
    </AModal>
  );
};

export default SendModal;
